import type {ApiWorkforceProfileRecord, ApiCompetencyRow} from '../WorkforceProfiles/workforceProfileApiTypes';

import type {WorkforceProfileCompetencyRow} from '../../types/WorkforceProfileTypes';


const resolveSkillName = (
  skill: string | {en?: string; de?: string}
): {name: string; nameEn?: string; nameDe?: string} => {
  if (typeof skill === 'string') {
    return {name: skill};
  }

  const en = skill.en?.trim() || '';
  const de = skill.de?.trim() || '';

  return {
    name: en || de,
    nameEn: en || undefined,
    nameDe: de || undefined,
  };
};

const hasSkillName = (row: ApiCompetencyRow): boolean => {
  if (typeof row.skill === 'string') return true;
  if (typeof row.skill === 'object' && row.skill != null) {
    return !!(row.skill.en?.trim() || row.skill.de?.trim());
  }
  return false;
};

export const mapCompetencyRows = (rows: ApiCompetencyRow[] | undefined): WorkforceProfileCompetencyRow[] =>
  (rows ?? []).filter(hasSkillName).map((row) => {
    const explanation = row.explanation?.trim() || row.justification?.trim() || null;
    const names = resolveSkillName(row.skill as string | {en?: string; de?: string});

    return {
      ...names,
      met: Boolean(row.met),
      explanation,
    };
  });

const sortCompetencyRowsMetFirst = (rows: WorkforceProfileCompetencyRow[]): WorkforceProfileCompetencyRow[] =>
  [...rows].sort((a, b) => Number(b.met) - Number(a.met));

// Prefer the v2 section name; fall back to the legacy v1 section while the API soaks the rollout.
const alignmentSectionRows = (
  analysis: ApiWorkforceProfileRecord | undefined,
  v2Key: string,
  legacyKey?: string
): ApiCompetencyRow[] | undefined => {
  const v2Rows = (analysis?.[v2Key] as ApiWorkforceProfileRecord | undefined)?.rows as
    | ApiCompetencyRow[]
    | undefined;
  if (v2Rows?.length) {
    return v2Rows;
  }

  return legacyKey
    ? ((analysis?.[legacyKey] as ApiWorkforceProfileRecord | undefined)?.rows as ApiCompetencyRow[] | undefined)
    : undefined;
};

export const buildCoreCompetencyRows = (
  analysis: ApiWorkforceProfileRecord | undefined
): WorkforceProfileCompetencyRow[] => {
  if (!analysis) {
    return [];
  }

  const coreRows = alignmentSectionRows(analysis, 'coreCompetenciesAlignment', 'skillsCompetenciesAlignment');
  if (coreRows?.length) {
    return sortCompetencyRowsMetFirst(mapCompetencyRows(coreRows));
  }

  return sortCompetencyRowsMetFirst([
    ...mapCompetencyRows(analysis.mustHaveSkillFit as ApiCompetencyRow[] | undefined),
    ...mapCompetencyRows(analysis.niceToHaveSkillFit as ApiCompetencyRow[] | undefined),
  ]);
};

export const buildHardSkillRows = (
  analysis: ApiWorkforceProfileRecord | undefined
): WorkforceProfileCompetencyRow[] => {
  if (!analysis) {
    return [];
  }

  return sortCompetencyRowsMetFirst(
    mapCompetencyRows(alignmentSectionRows(analysis, 'hardSkillsAlignment', 'knowledgeSkillsAlignment'))
  );
};

export const buildSoftSkillRows = (
  analysis: ApiWorkforceProfileRecord | undefined
): WorkforceProfileCompetencyRow[] => {
  if (!analysis) {
    return [];
  }

  return sortCompetencyRowsMetFirst(mapCompetencyRows(alignmentSectionRows(analysis, 'softSkillsAlignment')));
};

export const buildToolsRows = (
  analysis: ApiWorkforceProfileRecord | undefined
): WorkforceProfileCompetencyRow[] => {
  if (!analysis) {
    return [];
  }

  return sortCompetencyRowsMetFirst(mapCompetencyRows(alignmentSectionRows(analysis, 'toolsAlignment')));
};

export const buildDomainKnowledgeRows = (
  analysis: ApiWorkforceProfileRecord | undefined
): WorkforceProfileCompetencyRow[] => {
  if (!analysis) {
    return [];
  }

  return sortCompetencyRowsMetFirst(mapCompetencyRows(alignmentSectionRows(analysis, 'domainKnowledgeAlignment')));
};

export const buildResponsibilitiesRows = (
  analysis: ApiWorkforceProfileRecord | undefined
): WorkforceProfileCompetencyRow[] => {
  if (!analysis) {
    return [];
  }

  return sortCompetencyRowsMetFirst(mapCompetencyRows(alignmentSectionRows(analysis, 'responsibilitiesAlignment')));
};
